import { ui as uiFn } from './i18n';

/** Wrapper to provide consistent destructuring pattern for i18n ui function */
function await_ui() { return { ui: uiFn }; }

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

const MONTH_KEYS = [
  'ui.month.january', 'ui.month.february', 'ui.month.march', 'ui.month.april',
  'ui.month.may', 'ui.month.june', 'ui.month.july', 'ui.month.august',
  'ui.month.september', 'ui.month.october', 'ui.month.november', 'ui.month.december',
] as const;

/** Returns translated month name (0-indexed). Falls back to English. */
export function getMonthName(index: number, lang?: string): string {
  if (!lang || lang === 'en') return MONTHS[index] ?? '';
  const { ui } = await_ui();
  const key = MONTH_KEYS[index];
  if (!key) return MONTHS[index] ?? '';
  const resolved = ui(key, lang);
  return resolved !== key ? resolved : (MONTHS[index] ?? '');
}

export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const DAY_KEYS = [
  'ui.day.sun', 'ui.day.mon', 'ui.day.tue', 'ui.day.wed',
  'ui.day.thu', 'ui.day.fri', 'ui.day.sat',
] as const;

/** Returns translated day abbreviation (0=Sun). Falls back to English. */
export function getDayName(index: number, lang?: string): string {
  if (!lang || lang === 'en') return DAYS[index] ?? '';
  const { ui } = await_ui();
  const key = DAY_KEYS[index];
  if (!key) return DAYS[index] ?? '';
  const resolved = ui(key, lang);
  return resolved !== key ? resolved : (DAYS[index] ?? '');
}

export const TYPE_LABELS: Record<string, string> = {
  public_holiday: 'Public Holiday',
  national_day: 'National Day',
  observance: 'Observance',
  cultural_festival: 'Festival',
  local_event: 'Local Event',
  seasonal: 'Seasonal',
};

export const EVENT_COLORS: Record<string, string> = {
  national_day: 'var(--event-national)',
  public_holiday: 'var(--event-holiday)',
  cultural_festival: 'var(--event-festival)',
  local_event: 'var(--event-event)',
  observance: 'var(--event-observance)',
  seasonal: 'var(--event-observance)',
  school_term_start: 'var(--event-event)',
  school_term_end: 'var(--event-event)',
  exam_period: 'var(--event-observance)',
  enrollment_deadline: 'var(--event-national)',
  school_holiday: 'var(--event-holiday)',
};

export const LEGEND_ITEMS = [
  { type: 'national_day', label: 'National Day', color: 'var(--event-national)' },
  { type: 'public_holiday', label: 'Public Holiday', color: 'var(--event-holiday)' },
  { type: 'cultural_festival', label: 'Festival', color: 'var(--event-festival)' },
  { type: 'local_event', label: 'Local Event', color: 'var(--event-event)' },
  { type: 'observance', label: 'Observance', color: 'var(--event-observance)' },
  { type: 'seasonal', label: 'Seasonal', color: 'var(--event-observance)' },
] as const;

export const LENS_COLORS: Record<string, string> = {
  country: '#c084fc',   // purple
  city: '#60a5fa',      // blue
  religion: '#34d399',  // green
  nature: '#38bdf8',    // sky
};

export const SIGNIFICANCE_CONFIG = {
  critical: { label: 'National Importance', color: '#dc2626', dot: 'bg-red-600' },
  highest: { label: 'Widely Observed', color: '#ef4444', dot: 'bg-red-500' },
  high: { label: 'Widely Observed', color: '#f97316', dot: 'bg-orange-500' },
  major: { label: 'Commonly Observed', color: '#f59e0b', dot: 'bg-amber-500' },
  notable: { label: 'Commonly Observed', color: '#d97706', dot: 'bg-amber-600' },
  medium: { label: 'Regionally Observed', color: '#eab308', dot: 'bg-yellow-500' },
  low: { label: 'Limited Observance', color: '#6b7280', dot: 'bg-gray-500' },
} as const;

export const BUSINESS_IMPACT_CONFIG = {
  critical: { label: 'Full Closure', color: '#dc2626', bg: 'bg-red-600/15' },
  high: { label: 'Offices Closed', color: '#ef4444', bg: 'bg-red-500/15' },
  medium: { label: 'Reduced Operations', color: '#eab308', bg: 'bg-yellow-500/15' },
  low: { label: 'Normal Operations', color: '#22c55e', bg: 'bg-green-500/15' },
} as const;

export const ACADEMIC_TYPE_LABELS: Record<string, string> = {
  school_term_start: 'Term Start',
  school_term_end: 'Term End',
  exam_period: 'Exams',
  enrollment_deadline: 'Enrollment',
  school_holiday: 'Holiday',
};

const ACADEMIC_TYPE_KEYS: Record<string, string> = {
  school_term_start: 'ui.academic.term_start',
  school_term_end: 'ui.academic.term_end',
  exam_period: 'ui.academic.exams',
  enrollment_deadline: 'ui.academic.enrollment',
  school_holiday: 'ui.academic.holiday',
};

/** Returns translated academic type label. Falls back to English. */
export function getAcademicTypeLabel(type: string, lang?: string): string {
  if (lang && lang !== 'en') {
    const { ui } = await_ui();
    const key = ACADEMIC_TYPE_KEYS[type];
    if (key) {
      const resolved = ui(key, lang);
      if (resolved !== key) return resolved;
    }
  }
  return ACADEMIC_TYPE_LABELS[type] ?? type;
}

/** Returns a translated type label for the given language */
export function getTypeLabel(type: string, lang?: string): string {
  if (lang) {
    const { ui } = await_ui();
    const key = `ui.type.${type}`;
    const resolved = ui(key, lang);
    if (resolved !== key) return resolved;
  }
  return TYPE_LABELS[type] ?? ACADEMIC_TYPE_LABELS[type] ?? type;
}

/** Returns translated legend items for the given language */
export function getLegendItems(lang: string = 'en') {
  const { ui } = await_ui();
  return LEGEND_ITEMS.map(item => ({
    ...item,
    label: (() => {
      const key = `ui.${item.type}`;
      const resolved = ui(key, lang);
      return resolved !== key ? resolved : item.label;
    })(),
  }));
}

/** Returns translated significance label */
export function getSignificanceLabel(level: string, lang: string = 'en'): string {
  const { ui } = await_ui();
  const key = `ui.significance.${level}`;
  const resolved = ui(key, lang);
  return resolved !== key ? resolved : (SIGNIFICANCE_CONFIG[level as keyof typeof SIGNIFICANCE_CONFIG]?.label ?? level);
}

/** Returns translated business impact label */
export function getImpactLabel(level: string, lang: string = 'en'): string {
  const { ui } = await_ui();
  const key = `ui.impact.${level}`;
  const resolved = ui(key, lang);
  return resolved !== key ? resolved : (BUSINESS_IMPACT_CONFIG[level as keyof typeof BUSINESS_IMPACT_CONFIG]?.label ?? level);
}

export function getEventColor(type: string): string {
  return EVENT_COLORS[type] ?? 'var(--event-observance)';
}

export type CountryFlag = { code: string; flag: string; label: string };

export const COUNTRY_LIST: CountryFlag[] = [
  { code: 'ID', flag: '\u{1F1EE}\u{1F1E9}', label: 'Indonesia' },
  { code: 'IN', flag: '\u{1F1EE}\u{1F1F3}', label: 'India' },
  { code: 'MY', flag: '\u{1F1F2}\u{1F1FE}', label: 'Malaysia' },
  { code: 'SG', flag: '\u{1F1F8}\u{1F1EC}', label: 'Singapore' },
  { code: 'AF', flag: '\u{1F1E6}\u{1F1EB}', label: 'Afghanistan' },
  { code: 'ET', flag: '\u{1F1EA}\u{1F1F9}', label: 'Ethiopia' },
  { code: 'MM', flag: '\u{1F1F2}\u{1F1F2}', label: 'Myanmar' },
  { code: 'MN', flag: '\u{1F1F2}\u{1F1F3}', label: 'Mongolia' },
  { code: 'LA', flag: '\u{1F1F1}\u{1F1E6}', label: 'Laos' },
  { code: 'IQ', flag: '\u{1F1EE}\u{1F1F6}', label: 'Iraq' },
  { code: 'SD', flag: '\u{1F1F8}\u{1F1E9}', label: 'Sudan' },
  { code: 'YE', flag: '\u{1F1FE}\u{1F1EA}', label: 'Yemen' },
  { code: 'SY', flag: '\u{1F1F8}\u{1F1FE}', label: 'Syria' },
  { code: 'XR', flag: '\u{1F3D4}\uFE0F', label: 'Tibet' },
];
